import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Users, ArrowLeft, Loader2, CalendarDays, Download, KeyRound, CheckCircle2, Store, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const DAY_IT = {
  "2026-12-11": "Venerdì 11 dicembre",
  "2026-12-12": "Sabato 12 dicembre",
  "2026-12-13": "Domenica 13 dicembre",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("nypf_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("visitors");
  const [visitors, setVisitors] = useState(null);
  const [exhibitors, setExhibitors] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const loadAll = () => {
    setLoadError("");
    axios
      .get(`${API}/admin/visitors`, authHeaders())
      .then((r) => setVisitors(r.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("nypf_admin_token");
          setToken("");
        } else setLoadError(formatDetail(err.response?.data?.detail));
      });
    axios
      .get(`${API}/admin/exhibitors`, authHeaders())
      .then((r) => setExhibitors(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (token) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("nypf_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("nypf_admin_token");
    setToken("");
    setVisitors(null);
    setExhibitors(null);
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa riga?")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, authHeaders());
      setVisitors((v) => {
        if (!v) return v;
        const removed = v.reservations.find((r) => r.id === id);
        return {
          ...v,
          reservations: v.reservations.filter((r) => r.id !== id),
          count: removed ? v.count - 1 : v.count,
          total_guests: removed ? v.total_guests - (removed.guests || 0) : v.total_guests,
        };
      });
      setExhibitors((x) => {
        if (!x) return x;
        const removed = x.applications.find((r) => r.id === id);
        return { ...x, applications: x.applications.filter((r) => r.id !== id), count: removed ? x.count - 1 : x.count };
      });
      toast.success("Riga eliminata.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    let header, rows, filename;
    if (tab === "visitors") {
      if (!visitors) return;
      header = ["Nome", "Email", "Partecipanti", "Giorno", "Tipo", "Prenotata il"];
      rows = visitors.reservations.map((r) => [
        r.name,
        r.email,
        r.guests,
        DAY_IT[r.day] || r.day,
        r.type === "masterclass" ? "Panettone Class" : "Visitatore",
        fmtDate(r.created_at),
      ]);
      filename = "prenotazioni-visitatori-nypf.csv";
    } else {
      if (!exhibitors) return;
      header = ["Attività", "Referente", "Email", "Telefono", "Sito/Instagram", "Prodotti", "Ricevuta il"];
      rows = exhibitors.applications.map((r) => [r.business_name, r.contact_name, r.email, r.phone, r.website || "", r.description, fmtDate(r.created_at)]);
      filename = "candidature-espositori-nypf.csv";
    }
    const csv = "﻿" + [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.next.length < 8) {
      setPwMsg({ ok: false, text: "La nuova password deve avere almeno 8 caratteri." });
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwMsg({ ok: false, text: "Le due password non coincidono." });
      return;
    }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next }, authHeaders());
      setPwMsg({ ok: true, text: "Password aggiornata con successo." });
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwMsg({ ok: false, text: formatDetail(err.response?.data?.detail) });
    } finally {
      setPwLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6 text-cream" data-testid="admin-page">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-cream/15 p-10">
          <Lock size={24} className="text-gold" />
          <h1 className="mt-6 font-serif text-3xl font-medium italic">Area Riservata</h1>
          <p className="mt-2 text-sm text-cream/55">New York Panettone Festival — prenotazioni e candidature</p>
          <label className="mt-8 block text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/50" htmlFor="admin-email">
            Email
          </label>
          <input id="admin-email" type="email" required data-testid="admin-email-input" value={email}
            onChange={(e) => setEmail(e.target.value)} className="field-input-dark mt-1" />
          <label className="mt-6 block text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/50" htmlFor="admin-password">
            Password
          </label>
          <input id="admin-password" type="password" required data-testid="admin-password-input" value={password}
            onChange={(e) => setPassword(e.target.value)} className="field-input-dark mt-1" />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-[#e8908f]">{error}</p>
          )}
          <button type="submit" data-testid="admin-login-btn" disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-cream disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            Accedi
          </button>
          <a href="/" data-testid="admin-back-home" className="mt-6 flex items-center justify-center gap-2 text-xs text-cream/45 transition-colors hover:text-gold">
            <ArrowLeft size={12} /> Torna al sito
          </a>
        </form>
      </div>
    );
  }

  const currentEmpty = tab === "visitors" ? visitors && visitors.reservations.length === 0 : exhibitors && exhibitors.applications.length === 0;

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid="admin-page">
      <div className="mx-auto max-w-[1200px] px-6 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Area Riservata</p>
            <h1 className="mt-3 font-serif text-4xl font-medium italic" data-testid="admin-title">
              {tab === "visitors" ? "Prenotazioni Visitatori" : "Candidature Espositori"}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={exportCsv} data-testid="admin-export-btn" disabled={!!currentEmpty}
              className="flex items-center gap-2 bg-gold px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink transition-colors hover:bg-cream disabled:opacity-40">
              <Download size={13} /> Scarica CSV
            </button>
            <button onClick={loadAll} data-testid="admin-refresh-btn"
              className="border border-cream/25 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors hover:border-gold hover:text-gold">
              Aggiorna
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="flex items-center gap-2 bg-panettone px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors hover:bg-cream hover:text-ink">
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>

        <div className="mt-10 flex border-b border-cream/15" data-testid="admin-tabs">
          {[
            { k: "visitors", label: "Visitatori", count: visitors?.count },
            { k: "exhibitors", label: "Espositori", count: exhibitors?.count },
          ].map((tb) => (
            <button key={tb.k} data-testid={`admin-tab-${tb.k}`} onClick={() => setTab(tb.k)}
              className={`relative px-2 py-4 pr-8 text-left font-serif text-xl italic transition-colors duration-300 ${
                tab === tb.k ? "text-gold" : "text-cream/40 hover:text-cream/70"
              }`}>
              {tb.label}
              {tb.count != null && <span className="ml-2 text-sm not-italic text-cream/50">({tb.count})</span>}
              <span className={`absolute bottom-0 left-0 h-0.5 bg-gold transition-[width] duration-500 ${tab === tb.k ? "w-full" : "w-0"}`} />
            </button>
          ))}
        </div>

        {tab === "visitors" && visitors && (
          <div className="mt-8 flex flex-wrap gap-8 border-b border-cream/15 pb-5" data-testid="admin-stats">
            <p className="flex items-center gap-2 text-sm text-cream/70">
              <Users size={15} className="text-gold" />
              <strong className="font-serif text-2xl text-cream">{visitors.count}</strong> prenotazioni
            </p>
            <p className="flex items-center gap-2 text-sm text-cream/70">
              <CalendarDays size={15} className="text-gold" />
              <strong className="font-serif text-2xl text-cream">{visitors.total_guests}</strong> partecipanti totali
            </p>
          </div>
        )}
        {tab === "exhibitors" && exhibitors && (
          <div className="mt-8 flex flex-wrap gap-8 border-b border-cream/15 pb-5" data-testid="admin-stats-exhibitors">
            <p className="flex items-center gap-2 text-sm text-cream/70">
              <Store size={15} className="text-gold" />
              <strong className="font-serif text-2xl text-cream">{exhibitors.count}</strong> candidature
            </p>
          </div>
        )}
        {loadError && <p className="mt-4 text-sm text-[#e8908f]" data-testid="admin-load-error">{loadError}</p>}

        {tab === "visitors" && (
          <div className="mt-8 overflow-x-auto" data-testid="admin-table-wrap">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-cream/15 text-[10px] font-semibold uppercase tracking-[0.22em] text-cream/45">
                  <th className="py-3 pr-6">Nome</th>
                  <th className="py-3 pr-6">Email</th>
                  <th className="py-3 pr-6">Partecipanti</th>
                  <th className="py-3 pr-6">Giorno</th>
                  <th className="py-3 pr-6">Prenotata il</th>
                  <th className="py-3" />
                </tr>
              </thead>
              <tbody data-testid="admin-table">
                {visitors && visitors.reservations.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center italic text-cream/40">Nessuna prenotazione ancora ricevuta.</td></tr>
                )}
                {visitors && visitors.reservations.map((r) => (
                  <tr key={r.id} className="border-b border-cream/8 transition-colors hover:bg-cream/5" data-testid={`admin-row-${r.id}`}>
                    <td className="py-3.5 pr-6 font-medium text-cream">{r.name}</td>
                    <td className="py-3.5 pr-6 text-cream/70">{r.email}</td>
                    <td className="py-3.5 pr-6 text-cream/70">{r.guests}</td>
                    <td className="py-3.5 pr-6 text-cream/70">
                      {DAY_IT[r.day] || r.day}
                      {r.type === "masterclass" && (
                        <span className="ml-2 inline-block border border-gold/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold">
                          Class
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 pr-6 text-cream/50">{fmtDate(r.created_at)}</td>
                    <td className="py-3.5">
                      <button onClick={() => deleteSubmission(r.id)} data-testid={`admin-delete-${r.id}`}
                        aria-label="Elimina" title="Elimina"
                        className="p-1.5 text-cream/35 transition-colors hover:text-[#e8908f]">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "exhibitors" && (
          <div className="mt-8 overflow-x-auto" data-testid="admin-exhibitors-wrap">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-cream/15 text-[10px] font-semibold uppercase tracking-[0.22em] text-cream/45">
                  <th className="py-3 pr-6">Attività</th>
                  <th className="py-3 pr-6">Referente</th>
                  <th className="py-3 pr-6">Email</th>
                  <th className="py-3 pr-6">Telefono</th>
                  <th className="py-3 pr-6">Sito / Instagram</th>
                  <th className="py-3 pr-6">Prodotti</th>
                  <th className="py-3 pr-6">Ricevuta il</th>
                  <th className="py-3" />
                </tr>
              </thead>
              <tbody data-testid="admin-exhibitors-table">
                {exhibitors && exhibitors.applications.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center italic text-cream/40">Nessuna candidatura ancora ricevuta.</td></tr>
                )}
                {exhibitors && exhibitors.applications.map((r) => (
                  <tr key={r.id} className="border-b border-cream/8 align-top transition-colors hover:bg-cream/5" data-testid={`admin-exh-row-${r.id}`}>
                    <td className="py-3.5 pr-6 font-medium text-cream">{r.business_name}</td>
                    <td className="py-3.5 pr-6 text-cream/70">{r.contact_name}</td>
                    <td className="py-3.5 pr-6 text-cream/70">{r.email}</td>
                    <td className="py-3.5 pr-6 text-cream/70">{r.phone}</td>
                    <td className="py-3.5 pr-6 text-cream/70">{r.website || "—"}</td>
                    <td className="max-w-[280px] py-3.5 pr-6 text-cream/60" title={r.description}>
                      <span className="line-clamp-2">{r.description}</span>
                    </td>
                    <td className="py-3.5 pr-6 text-cream/50">{fmtDate(r.created_at)}</td>
                    <td className="py-3.5">
                      <button onClick={() => deleteSubmission(r.id)} data-testid={`admin-delete-${r.id}`}
                        aria-label="Elimina" title="Elimina"
                        className="p-1.5 text-cream/35 transition-colors hover:text-[#e8908f]">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={changePassword} data-testid="admin-password-form" className="mt-16 max-w-md border border-cream/15 p-8">
          <KeyRound size={20} className="text-gold" />
          <h2 className="mt-4 font-serif text-2xl font-medium italic">Cambia password</h2>
          <label className="mt-6 block text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/50" htmlFor="pw-current">
            Password attuale
          </label>
          <input id="pw-current" type="password" required data-testid="pw-current-input" value={pwForm.current}
            onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className="field-input-dark mt-1" />
          <label className="mt-5 block text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/50" htmlFor="pw-next">
            Nuova password
          </label>
          <input id="pw-next" type="password" required minLength={8} data-testid="pw-next-input" value={pwForm.next}
            onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className="field-input-dark mt-1" />
          <label className="mt-5 block text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/50" htmlFor="pw-confirm">
            Conferma nuova password
          </label>
          <input id="pw-confirm" type="password" required minLength={8} data-testid="pw-confirm-input" value={pwForm.confirm}
            onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="field-input-dark mt-1" />
          {pwMsg && (
            <p data-testid="pw-message" className={`mt-4 flex items-center gap-2 text-sm ${pwMsg.ok ? "text-[#9fd3a8]" : "text-[#e8908f]"}`}>
              {pwMsg.ok && <CheckCircle2 size={14} />} {pwMsg.text}
            </p>
          )}
          <button type="submit" data-testid="pw-submit-btn" disabled={pwLoading}
            className="mt-6 flex items-center gap-2 bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink transition-colors hover:bg-cream disabled:opacity-60">
            {pwLoading && <Loader2 size={13} className="animate-spin" />}
            Aggiorna password
          </button>
        </form>
      </div>
    </div>
  );
}
